import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { Customer } from 'src/app/models/customer';
import { IArticle } from 'src/app/models/interfaces/article.interface';
import { CustomersService } from 'src/app/services/customers.service';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { ArticleFormContext } from '../article-form-context';

@Component({
    selector: 'app-article-form',
    templateUrl: './article-form.component.html',
    styleUrls: ['./article-form.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, MatFormFieldModule, MatInputModule, FaIconComponent, MatTooltipModule, MatButtonModule, NgClass]
})
export class ArticleFormComponent extends ManagementFormComponent<IArticle> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('article')
    public dataInput: Required<IArticle> | null | undefined;

    public customers: Customer[] = [];

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ArticleFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ArticleFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ArticleFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('articleUpdated')
    public dataOutput: EventEmitter<IArticle> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Constructeur
     * @param customersService Instance du service des clients.
     */
    constructor(private customersService: CustomersService) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            this.customersService.List(null, null, null, null).subscribe((customers) => {
                this.customers = customers.value;
            });
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IArticle> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            code: new UntypedFormControl(null, Validators.required),
            designation1: new UntypedFormControl(null, Validators.required),
            designation2: new UntypedFormControl(null),
            unitPrice: new UntypedFormControl(null, Validators.pattern(this.unitPricePattern)),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
